import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Plus,
  ArrowUp,
  ArrowDown,
  GripVertical,
  Trash2,
  Pencil,
  Clock,
  CheckCircle2,
  AlertCircle,
  X,
  BookOpen,
  Sparkles,
  Layers,
  Save,
} from 'lucide-react';
import api from '../../../../api/client';
import { calculateInterviewDurationRequirements } from '../../../../utils/durationCalculator';
import QuestionBankPickerModal from '../../component/QuestionBankPickerModal';
import AIQuestionGeneratorModal from '../../component/AIQuestionGeneratorModal';
import {
  MAX_PROFILE_QUESTIONS,
  getCategoryBreakdown,
  profileQuestionToJdQuestion,
} from './interviewProfileUtils';
import {
  getQuestionTypeColor,
  getQuestionTypeOptions,
} from '../questionBank/questionBankUtils';

export default function ProfileQuestionStudio({ open, onClose, profileId, onProfileUpdated }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [savingReorder, setSavingReorder] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [deleteTargetQuestion, setDeleteTargetQuestion] = useState(null);
  const [deletingQuestion, setDeletingQuestion] = useState(false);
  const [showBankPicker, setShowBankPicker] = useState(false);
  const [showAiGenerator, setShowAiGenerator] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const scrollContainerRef = useRef(null);

  // Auto-dismiss green success message after 3.5 seconds
  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => {
        setSuccessMsg('');
      }, 3500);
      return () => clearTimeout(timer);
    }
  }, [successMsg]);

  // Auto-dismiss red error message after 5 seconds
  useEffect(() => {
    if (errorMsg) {
      const timer = setTimeout(() => {
        setErrorMsg('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMsg]);

  // Question Form State
  const [qForm, setQForm] = useState({
    text: '',
    question_type: 'concept',
    time_limit: 5,
    mcqOptions: ['', '', '', ''],
    mcqCorrectIndex: 0,
    save_to_question_bank: false,
  });
  const [submittingQuestion, setSubmittingQuestion] = useState(false);

  const fetchProfileDetails = useCallback(async () => {
    if (!profileId) return;
    setLoading(true);
    try {
      const res = await api.get(`interview-profiles/${profileId}/`);
      setProfile(res.data);
      setQuestions(res.data.profile_questions || []);
    } catch (err) {
      console.error('Failed to fetch profile details:', err);
      setErrorMsg('Failed to load profile details.');
    } finally {
      setLoading(false);
    }
  }, [profileId]);

  useEffect(() => {
    if (open && profileId) {
      fetchProfileDetails();
      setErrorMsg('');
      setSuccessMsg('');
    }
  }, [open, profileId, fetchProfileDetails]);

  // Duration calculations
  const durationInfo = useMemo(() => {
    const jdFormatted = questions.map((q) => profileQuestionToJdQuestion(q));
    return calculateInterviewDurationRequirements(jdFormatted);
  }, [questions]);

  // Category breakdown
  const breakdown = useMemo(() => {
    return getCategoryBreakdown(questions);
  }, [questions]);

  // Profile skills extracted from profile data & existing question skill_tags
  const profileSkills = useMemo(() => {
    const list = [
      ...(Array.isArray(profile?.skills) ? profile.skills : []),
      ...(Array.isArray(profile?.required_skills) ? profile.required_skills : []),
      ...(typeof profile?.skills === 'string' ? profile.skills.split(',').map((s) => s.trim()) : []),
      ...((questions || []).map((q) => q.skill_tag || q.skillTag).filter(Boolean).map((s) => s.trim())),
    ];
    return Array.from(new Set(list)).filter(Boolean);
  }, [profile, questions]);

  const isFull = questions.length >= MAX_PROFILE_QUESTIONS;

  // Open modal for adding question
  const handleOpenAddQuestion = () => {
    if (isFull) return;
    setEditingQuestion(null);
    setQForm({
      text: '',
      question_type: 'concept',
      time_limit: 5,
      mcqOptions: ['', '', '', ''],
      mcqCorrectIndex: 0,
      save_to_question_bank: false,
    });
    setShowQuestionModal(true);
  };

  // Open modal for editing question
  const handleOpenEditQuestion = (q) => {
    setEditingQuestion(q);
    const rawChoices = Array.isArray(q.choices) ? q.choices : [];
    const correctIdx = rawChoices.findIndex((c) => c.is_correct);

    setQForm({
      text: q.text || '',
      question_type: q.question_type || 'concept',
      time_limit: q.time_limit || 5,
      mcqOptions: [0, 1, 2, 3].map((i) => rawChoices[i]?.text || ''),
      mcqCorrectIndex: correctIdx >= 0 ? correctIdx : 0,
      save_to_question_bank: false,
    });
    setShowQuestionModal(true);
  };

  // Save (create or update) question
  const handleSaveQuestion = async (e) => {
    e.preventDefault();
    if (!qForm.text.trim()) return;

    let payloadChoices = [];
    if (qForm.question_type === 'mcq') {
      const validOptions = qForm.mcqOptions.map((opt, i) => ({
        text: opt.trim(),
        is_correct: qForm.mcqCorrectIndex === i,
      })).filter((c) => c.text);

      if (validOptions.length < 2) {
        setErrorMsg('MCQ questions require at least 2 non-empty options.');
        return;
      }
      payloadChoices = validOptions;
    }

    const payload = {
      text: qForm.text.trim(),
      question_type: qForm.question_type,
      time_limit: qForm.question_type === 'coding' ? Number(qForm.time_limit) || 5 : 5,
      choices: payloadChoices,
      save_to_question_bank: qForm.save_to_question_bank,
    };

    setSubmittingQuestion(true);
    setErrorMsg('');
    try {
      if (editingQuestion) {
        await api.patch(`interview-profiles/${profileId}/questions/${editingQuestion.id}/`, payload);
        setSuccessMsg('Question updated successfully.');
      } else {
        await api.post(`interview-profiles/${profileId}/questions/`, payload);
        setSuccessMsg('Question added successfully.');
      }
      setShowQuestionModal(false);
      await fetchProfileDetails();
      if (onProfileUpdated) onProfileUpdated();
    } catch (err) {
      console.error('Failed to save question:', err);
      const data = err.response?.data;
      let detail = 'Failed to save question.';
      if (typeof data === 'string') {
        detail = data;
      } else if (data) {
        detail = data.detail || data.error || (data.text && data.text[0]) || (data.choices && data.choices[0]) || Object.values(data)[0] || detail;
        if (Array.isArray(detail)) detail = detail[0];
      }
      setErrorMsg(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setSubmittingQuestion(false);
    }
  };

  // Delete question confirmation
  const handleConfirmDelete = async () => {
    if (!deleteTargetQuestion) return;
    setDeletingQuestion(true);
    try {
      await api.delete(`interview-profiles/${profileId}/questions/${deleteTargetQuestion.id}/`);
      setSuccessMsg('Question removed from profile.');
      setDeleteTargetQuestion(null);
      await fetchProfileDetails();
      if (onProfileUpdated) onProfileUpdated();
    } catch (err) {
      console.error('Failed to delete question:', err);
      setErrorMsg('Failed to delete question.');
    } finally {
      setDeletingQuestion(false);
    }
  };

  // Move Up / Move Down Reordering
  const handleMoveQuestion = async (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= questions.length) return;

    const newOrder = [...questions];
    const [moved] = newOrder.splice(index, 1);
    newOrder.splice(targetIndex, 0, moved);

    setQuestions(newOrder); // Optimistic UI update

    const questionIds = newOrder.map((q) => q.id);
    setSavingReorder(true);
    try {
      await api.post(`interview-profiles/${profileId}/reorder/`, { question_ids: questionIds });
      if (onProfileUpdated) onProfileUpdated();
    } catch (err) {
      console.error('Failed to save reorder:', err);
      setErrorMsg('Failed to persist question order.');
      await fetchProfileDetails();
    } finally {
      setSavingReorder(false);
    }
  };

  // Auto-scroll scrollable list while dragging near top/bottom boundaries
  const triggerAutoScroll = (clientY) => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const rect = container.getBoundingClientRect();
    const edgeThreshold = 90; // px threshold from top/bottom

    if (clientY < rect.top + edgeThreshold && clientY >= rect.top - 30) {
      // Near top edge: scroll up
      const distance = Math.max(1, rect.top + edgeThreshold - clientY);
      const step = Math.min(26, Math.max(6, Math.round((distance / edgeThreshold) * 26)));
      container.scrollTop -= step;
    } else if (clientY > rect.bottom - edgeThreshold && clientY <= rect.bottom + 30) {
      // Near bottom edge: scroll down
      const distance = Math.max(1, clientY - (rect.bottom - edgeThreshold));
      const step = Math.min(26, Math.max(6, Math.round((distance / edgeThreshold) * 26)));
      container.scrollTop += step;
    }
  };

  const handleContainerDragOver = (e) => {
    e.preventDefault();
    triggerAutoScroll(e.clientY);
  };

  // Drag and Drop Reordering Handlers with direction awareness
  const [dropPosition, setDropPosition] = useState('bottom'); // 'top' | 'bottom'

  const handleDragStart = (e, index) => {
    if (savingReorder) return;
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    try {
      e.dataTransfer.setData('text/plain', String(index));
    } catch {
      // fallback
    }
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    // Calculate whether cursor is in the top or bottom half of the target card
    const rect = e.currentTarget.getBoundingClientRect();
    const midpoint = rect.top + rect.height / 2;
    const pos = e.clientY < midpoint ? 'top' : 'bottom';
    setDropPosition(pos);

    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }

    triggerAutoScroll(e.clientY);
  };

  const handleDragLeave = (e, index) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    if (dragOverIndex === index) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = async (e, targetIndex) => {
    e.preventDefault();
    const sourceIndex = draggedIndex;
    const pos = dropPosition;

    setDraggedIndex(null);
    setDragOverIndex(null);

    if (sourceIndex === null || sourceIndex === undefined) return;

    // Calculate final insertion index
    let finalTargetIndex = targetIndex;
    if (pos === 'bottom' && sourceIndex < targetIndex) {
      finalTargetIndex = targetIndex;
    } else if (pos === 'bottom' && sourceIndex > targetIndex) {
      finalTargetIndex = targetIndex + 1;
    } else if (pos === 'top' && sourceIndex < targetIndex) {
      finalTargetIndex = Math.max(0, targetIndex - 1);
    } else if (pos === 'top' && sourceIndex > targetIndex) {
      finalTargetIndex = targetIndex;
    }

    if (sourceIndex === finalTargetIndex) return;

    const newOrder = [...questions];
    const [movedItem] = newOrder.splice(sourceIndex, 1);
    newOrder.splice(finalTargetIndex, 0, movedItem);

    setQuestions(newOrder); // Optimistic UI update

    const questionIds = newOrder.map((q) => q.id);
    setSavingReorder(true);
    try {
      await api.post(`interview-profiles/${profileId}/reorder/`, { question_ids: questionIds });
      if (onProfileUpdated) onProfileUpdated();
    } catch (err) {
      console.error('Failed to save drag-and-drop reorder:', err);
      setErrorMsg('Failed to persist question order.');
      await fetchProfileDetails();
    } finally {
      setSavingReorder(false);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Import from Question Bank
  const handleBankQuestionsAdded = async (bankQuestions) => {
    if (!bankQuestions || bankQuestions.length === 0) return;
    setShowBankPicker(false);

    const availableSlots = MAX_PROFILE_QUESTIONS - questions.length;
    if (availableSlots <= 0) {
      setErrorMsg(`Profile is full. Maximum ${MAX_PROFILE_QUESTIONS} questions reached.`);
      return;
    }

    const toAdd = bankQuestions.slice(0, availableSlots);
    setLoading(true);
    try {
      for (const item of toAdd) {
        const payload = {
          company_question_id: item.questionBankId || item.id,
          text: item.nameOfQuestion || item.text,
          question_type: item.type || item.question_type || 'concept',
          time_limit: item.time_limit || 5,
          choices: item.choices || [],
        };
        await api.post(`interview-profiles/${profileId}/questions/`, payload);
      }
      setSuccessMsg(`Successfully imported ${toAdd.length} questions from Question Bank.`);
      await fetchProfileDetails();
      if (onProfileUpdated) onProfileUpdated();
    } catch (err) {
      console.error('Failed importing from Question Bank:', err);
      setErrorMsg('Some questions could not be imported.');
      await fetchProfileDetails();
    } finally {
      setLoading(false);
    }
  };

  // Handle AI Generated Questions Added
  const handleAiQuestionsAdded = async (aiQuestions) => {
    if (!aiQuestions || aiQuestions.length === 0) return;
    setShowAiGenerator(false);

    const availableSlots = MAX_PROFILE_QUESTIONS - questions.length;
    if (availableSlots <= 0) {
      setErrorMsg(`Profile is full. Maximum ${MAX_PROFILE_QUESTIONS} questions reached.`);
      return;
    }

    const toAdd = aiQuestions.slice(0, availableSlots);
    setLoading(true);
    try {
      for (const item of toAdd) {
        const payload = {
          company_question_id: item.questionBankId || null,
          text: item.nameOfQuestion || item.text || item.question,
          question_type: item.type || item.question_type || 'concept',
          time_limit: item.time_limit || 5,
          choices: item.choices || [],
          skill_tag: item.skill_tag || '',
          difficulty: item.difficulty === 'easy' ? 1 : item.difficulty === 'hard' ? 3 : 2,
        };
        await api.post(`interview-profiles/${profileId}/questions/`, payload);
      }
      setSuccessMsg(`Successfully added ${toAdd.length} AI-generated questions to profile.`);
      await fetchProfileDetails();
      if (onProfileUpdated) onProfileUpdated();
    } catch (err) {
      console.error('Failed adding AI questions to profile:', err);
      setErrorMsg('Some AI questions could not be added.');
      await fetchProfileDetails();
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            minHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Header */}
        <DialogTitle className="flex items-center justify-between border-b pb-3 bg-slate-50/80">
          <div className="flex items-center gap-3">
            <div
              className="p-2.5 text-white rounded-xl shadow-xs"
              style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}
            >
              <Layers className="w-6 h-6 stroke-[2.2]" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold text-gray-900">{profile?.name || 'Interview Profile Studio'}</h2>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">
                  {profile?.target_role || 'General Role'}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                {profile?.description || 'Curate up to 30 questions to standardize interviews for this role.'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1.5 rounded-full hover:bg-gray-200 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </DialogTitle>

        {/* Studio Subheader: Stats & Action Buttons */}
        <div className="px-6 py-3 bg-white border-b flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {/* Question count pill with progress */}
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border text-sm font-semibold ${
              isFull ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-gray-50 border-gray-200 text-gray-700'
            }`}>
              <span>Questions:</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                isFull ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-800'
              }`}>
                {questions.length} / {MAX_PROFILE_QUESTIONS}
              </span>
            </div>

            {/* Estimated Duration pill */}
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-blue-200 bg-blue-50 text-blue-800 text-xs font-semibold">
              <Clock className="w-4 h-4 text-blue-600" />
              <span>Recommended Duration: {durationInfo.recommended_duration_minutes} min</span>
              <span className="text-gray-400 font-normal">(Min {durationInfo.minimum_duration_minutes} min)</span>
            </div>

            {/* Breakdown badges */}
            <div className="hidden md:flex items-center gap-1.5 text-xs">
              {breakdown.concept > 0 && (
                <span className="px-2 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200">
                  {breakdown.concept} Concept
                </span>
              )}
              {breakdown.coding > 0 && (
                <span className="px-2 py-0.5 rounded bg-orange-50 text-orange-700 border border-orange-200">
                  {breakdown.coding} Coding
                </span>
              )}
              {breakdown.mcq > 0 && (
                <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                  {breakdown.mcq} MCQ
                </span>
              )}
            </div>

            {savingReorder && (
              <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs font-semibold animate-pulse">
                <CircularProgress size={12} color="inherit" />
                <span>Saving order...</span>
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outlined"
              size="small"
              onClick={() => setShowBankPicker(true)}
              disabled={isFull}
              startIcon={<BookOpen className="w-4 h-4" />}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px' }}
            >
              Import from Question Bank
            </Button>

            <Button
              type="button"
              variant="outlined"
              size="small"
              onClick={() => setShowAiGenerator(true)}
              disabled={isFull}
              startIcon={<Sparkles className="w-4 h-4 text-purple-600" />}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '10px',
                borderColor: '#d8b4fe',
                color: '#7e22ce',
                backgroundColor: '#faf5ff',
                '&:hover': {
                  borderColor: '#a855f7',
                  backgroundColor: '#f3e8ff',
                },
              }}
            >
              Generate with AI
            </Button>

            <Tooltip title={isFull ? 'Maximum 30 questions reached' : ''}>
              <span>
                <Button
                  type="button"
                  variant="contained"
                  size="small"
                  onClick={handleOpenAddQuestion}
                  disabled={isFull}
                  startIcon={<Plus className="w-4 h-4" />}
                  sx={{
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #2563eb 0%, #9333ea 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1d4ed8 0%, #7e22ce 100%)',
                    },
                  }}
                >
                  Add Question
                </Button>
              </span>
            </Tooltip>
          </div>
        </div>

        {/* Notifications */}
        {errorMsg && (
          <div className="mx-6 mt-3 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
              <span>{errorMsg}</span>
            </div>
            <button onClick={() => setErrorMsg('')} className="text-red-500 font-bold">&times;</button>
          </div>
        )}
        {successMsg && (
          <div className="mx-6 mt-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
              <span>{successMsg}</span>
            </div>
            <button onClick={() => setSuccessMsg('')} className="text-emerald-500 font-bold">&times;</button>
          </div>
        )}

        {/* Questions List with Auto-Scroll on Drag */}
        <DialogContent
          ref={scrollContainerRef}
          onDragOver={handleContainerDragOver}
          className="p-6 bg-slate-50/50 flex-1 overflow-y-auto"
        >
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <CircularProgress size={36} />
            </div>
          ) : questions.length === 0 ? (
            <div className="text-center py-20 bg-white border border-dashed border-gray-300 rounded-2xl">
              <Layers className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-base font-semibold text-gray-800">No Questions in this Profile yet</h3>
              <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">
                Add up to 30 custom conceptual, coding, or multiple choice questions, or import from your Question Bank.
              </p>
              <div className="mt-5 flex items-center justify-center gap-3">
                <Button
                  variant="outlined"
                  onClick={() => setShowBankPicker(true)}
                  startIcon={<BookOpen className="w-4 h-4" />}
                  sx={{ textTransform: 'none', borderRadius: '10px' }}
                >
                  Import from Question Bank
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setShowAiGenerator(true)}
                  startIcon={<Sparkles className="w-4 h-4 text-purple-600" />}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '10px',
                    borderColor: '#d8b4fe',
                    color: '#7e22ce',
                    backgroundColor: '#faf5ff',
                    '&:hover': {
                      borderColor: '#a855f7',
                      backgroundColor: '#f3e8ff',
                    },
                  }}
                >
                  Generate with AI
                </Button>
                <Button
                  variant="contained"
                  onClick={handleOpenAddQuestion}
                  startIcon={<Plus className="w-4 h-4" />}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, #2563eb 0%, #9333ea 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1d4ed8 0%, #7e22ce 100%)',
                },
                  }}
                >
                  Create First Question
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2.5">
              {questions.map((q, idx) => {
                const typeColor = getQuestionTypeColor(q.question_type);
                const isDragging = draggedIndex === idx;
                const isDragOver = dragOverIndex === idx && draggedIndex !== idx;

                return (
                  <div
                    key={q.id || idx}
                    draggable={!savingReorder}
                    onDragStart={(e) => handleDragStart(e, idx)}
                    onDragOver={(e) => handleDragOver(e, idx)}
                    onDragLeave={(e) => handleDragLeave(e, idx)}
                    onDrop={(e) => handleDrop(e, idx)}
                    onDragEnd={handleDragEnd}
                    className={`rounded-xl border p-3 sm:p-3.5 transition-all duration-150 flex items-start gap-2.5 group relative select-none ${
                      isDragging
                        ? 'opacity-30 border-dashed border-2 border-indigo-400 bg-indigo-50/40 shadow-inner scale-[0.99]'
                        : isDragOver
                        ? 'bg-indigo-50/40 border-2 border-indigo-500 shadow-md ring-2 ring-indigo-400/40'
                        : 'bg-white border-gray-200 shadow-sm hover:border-indigo-200 hover:shadow-md'
                    }`}
                  >
                    {/* Visual Drop Indicator Line */}
                    {isDragOver && (
                      <div
                        className={`absolute left-3 right-3 h-1 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-full shadow-md z-20 animate-pulse pointer-events-none ${
                          dropPosition === 'top' ? '-top-1.5' : '-bottom-1.5'
                        }`}
                      />
                    )}

                    {/* Drag Handle & Reorder Controls */}
                    <div className="flex items-center gap-2 shrink-0 pt-0.5">
                      {/* Dedicated Grip Drag Handle */}
                      <div
                        className="cursor-grab active:cursor-grabbing p-1.5 -ml-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors flex items-center justify-center"
                        title="Drag up or down to reorder"
                      >
                        <GripVertical className="w-4 h-4 stroke-[2.3]" />
                      </div>

                      {/* Number & Arrows */}
                      <div className="flex flex-col items-center gap-1">
                        <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-700 font-bold text-xs flex items-center justify-center">
                          {idx + 1}
                        </span>
                        <div className="flex flex-col gap-0.5">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMoveQuestion(idx, -1);
                            }}
                            disabled={idx === 0 || savingReorder}
                            className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-20 text-gray-500 cursor-pointer"
                            title="Move Up"
                          >
                            <ArrowUp className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMoveQuestion(idx, 1);
                            }}
                            disabled={idx === questions.length - 1 || savingReorder}
                            className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-20 text-gray-500 cursor-pointer"
                            title="Move Down"
                          >
                            <ArrowDown className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Question Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${typeColor}`}>
                          {q.question_type}
                        </span>

                        {q.question_type === 'coding' && (
                          <span className="text-[11px] font-semibold text-orange-700 bg-orange-50 border border-orange-200 px-2 py-0.5 rounded flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {q.time_limit || 5} min limit
                          </span>
                        )}

                        {q.company_question && (
                          <span className="text-[11px] text-gray-500 bg-gray-50 border border-gray-200 px-2 py-0.5 rounded flex items-center gap-1">
                            <BookOpen className="w-3 h-3 text-gray-400" />
                            From Question Bank #{q.company_question}
                          </span>
                        )}
                      </div>

                      <p className="text-sm font-medium text-gray-900 whitespace-pre-wrap leading-relaxed select-text">
                        {q.text}
                      </p>

                      {/* MCQ Choices Preview */}
                      {q.question_type === 'mcq' && Array.isArray(q.choices) && q.choices.length > 0 && (
                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                          {q.choices.map((c, cIdx) => (
                            <div
                              key={cIdx}
                              className={`text-xs px-3 py-1.5 rounded-lg border flex items-center gap-2 ${
                                c.is_correct
                                  ? 'bg-emerald-50 border-emerald-300 text-emerald-900 font-semibold'
                                  : 'bg-gray-50 border-gray-200 text-gray-700'
                              }`}
                            >
                              <span className="font-bold text-[10px] text-gray-500">
                                {String.fromCharCode(65 + cIdx)}.
                              </span>
                              <span className="truncate flex-1">{c.text}</span>
                              {c.is_correct && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenEditQuestion(q);
                        }}
                        className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors cursor-pointer"
                        title="Edit Question"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTargetQuestion(q);
                        }}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                        title="Remove Question"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>

        <DialogActions className="border-t px-6 py-3 bg-white justify-between">
          <span className="text-xs text-gray-500 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block"></span>
            Profile updates automatically sync to all attached Job Descriptions (running interviews remain safely unchanged).
          </span>
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold text-xs shadow-xs transition-all cursor-pointer"
          >
            Done
          </button>
        </DialogActions>
      </Dialog>

      {/* Add / Edit Question Modal */}
      <Dialog open={showQuestionModal} onClose={() => setShowQuestionModal(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <form onSubmit={handleSaveQuestion}>
          <DialogTitle className="flex items-center justify-between border-b pb-3 font-bold text-gray-800">
            <span>{editingQuestion ? 'Edit Profile Question' : 'Add Question to Profile'}</span>
            <button
              type="button"
              onClick={() => setShowQuestionModal(false)}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-full"
            >
              <X className="w-5 h-5" />
            </button>
          </DialogTitle>

          <DialogContent className="pt-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}>
                  <InputLabel id="qtype-label">Question Category</InputLabel>
                  <Select
                    labelId="qtype-label"
                    label="Question Category"
                    value={qForm.question_type}
                    onChange={(e) => setQForm({ ...qForm, question_type: e.target.value })}
                    sx={{ borderRadius: '12px' }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: '12px',
                          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                          mt: 0.5,
                        },
                      },
                    }}
                  >
                    <MenuItem value="concept">Conceptual</MenuItem>
                    <MenuItem value="coding">Coding Assessment</MenuItem>
                    <MenuItem value="mcq">Multiple Choice (MCQ)</MenuItem>
                    <MenuItem value="behavioral">Behavioral</MenuItem>
                    <MenuItem value="general">General</MenuItem>
                  </Select>
                </FormControl>
              </div>

              {qForm.question_type === 'coding' && (
                <div className="md:col-span-1">
                  <TextField
                    fullWidth
                    type="number"
                    label="Time Limit (Minutes)"
                    value={qForm.time_limit}
                    onChange={(e) => setQForm({ ...qForm, time_limit: Math.max(1, Math.min(60, Number(e.target.value) || 5)) })}
                    inputProps={{ min: 1, max: 60 }}
                    helperText="Candidate coding timer (1-60 min)"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </div>
              )}
            </div>

            <TextField
              required
              fullWidth
              multiline
              rows={4}
              label="Question Text"
              placeholder="Enter the full question prompt..."
              value={qForm.text}
              onChange={(e) => setQForm({ ...qForm, text: e.target.value })}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            />

            {/* MCQ Choices editor */}
            {qForm.question_type === 'mcq' && (
              <div className="space-y-3 bg-slate-50 p-4 rounded-xl border border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Options & Correct Answer
                  </span>
                  <span className="text-xs text-gray-500">Select the radio button next to the correct answer.</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {qForm.mcqOptions.map((opt, i) => (
                    <div key={i} className="flex items-center gap-2 bg-white p-2 rounded-xl border border-gray-200">
                      <input
                        type="radio"
                        name="mcq-correct-choice"
                        checked={qForm.mcqCorrectIndex === i}
                        onChange={() => setQForm({ ...qForm, mcqCorrectIndex: i })}
                        className="w-4 h-4 text-indigo-600 focus:ring-indigo-500"
                        title={`Mark choice ${String.fromCharCode(65 + i)} as correct`}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label={`Option ${String.fromCharCode(65 + i)}`}
                        value={opt}
                        onChange={(e) => {
                          const updated = [...qForm.mcqOptions];
                          updated[i] = e.target.value;
                          setQForm({ ...qForm, mcqOptions: updated });
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Reverse Flow Option: Save to Question Bank too */}
            {!editingQuestion && (
              <label className="flex items-center gap-2.5 p-3 rounded-xl border border-indigo-100 bg-indigo-50/50 cursor-pointer hover:bg-indigo-50 transition-colors">
                <input
                  type="checkbox"
                  checked={qForm.save_to_question_bank}
                  onChange={(e) => setQForm({ ...qForm, save_to_question_bank: e.target.checked })}
                  className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-gray-300"
                />
                <div className="text-xs">
                  <span className="font-semibold text-indigo-900 block">Save this question to Question Bank too</span>
                  <span className="text-indigo-600">
                    Makes this question available in the company-wide repository for reuse across other profiles and JDs.
                  </span>
                </div>
              </label>
            )}
          </DialogContent>

          <DialogActions className="border-t px-6 py-3">
            <Button onClick={() => setShowQuestionModal(false)} disabled={submittingQuestion}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submittingQuestion || !qForm.text.trim()}
              sx={{
                borderRadius: '10px',
                px: 3,
                textTransform: 'none',
                fontWeight: 700,
                background: 'linear-gradient(135deg, #2563eb 0%, #9333ea 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1d4ed8 0%, #7e22ce 100%)',
                },
              }}
            >
              {submittingQuestion ? <CircularProgress size={20} color="inherit" /> : 'Save Question'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Import from Question Bank Picker */}
      <QuestionBankPickerModal
        open={showBankPicker}
        onClose={() => setShowBankPicker(false)}
        onAddQuestions={handleBankQuestionsAdded}
        existingQuestions={questions.map((q) => profileQuestionToJdQuestion(q))}
      />

      {/* AI Question Generator Modal (Reusing existing JD flow & API) */}
      <AIQuestionGeneratorModal
        open={showAiGenerator}
        onClose={() => setShowAiGenerator(false)}
        onAddQuestions={handleAiQuestionsAdded}
        jobTitle={profile?.target_role || profile?.name || 'Software Engineer'}
        experienceLevel={profile?.experience_level || 'mid'}
        requiredSkills={profileSkills}
        niceToHaveSkills={[]}
        profileId={profile?.id}
        existingQuestions={questions.map((q) => profileQuestionToJdQuestion(q))}
        companyId={profile?.company?.id || profile?.company}
        targetType="profile"
      />

      {/* Delete Question Confirmation Dialog (Centered Modal) */}
      <Dialog
        open={Boolean(deleteTargetQuestion)}
        onClose={() => !deletingQuestion && setDeleteTargetQuestion(null)}
        maxWidth="xs"
        fullWidth
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 30,
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(15, 23, 42, 0.5)',
            backdropFilter: 'blur(3px)',
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: '20px',
            p: 1.5,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <div className="w-11 h-11 rounded-2xl bg-red-100 flex items-center justify-center shrink-0 border border-red-200">
            <Trash2 className="w-5 h-5 text-red-600 stroke-[2.2]" />
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900 leading-tight">Remove Question?</h3>
            <p className="text-xs text-gray-500 mt-0.5">Are you sure you want to remove this question?</p>
          </div>
        </DialogTitle>

        <DialogContent sx={{ py: 2 }}>
          {deleteTargetQuestion && (
            <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 text-xs text-gray-800 leading-relaxed font-medium">
              <span className="font-bold text-indigo-700 block mb-1 uppercase text-[10px] tracking-wider">
                {deleteTargetQuestion.question_type}
              </span>
              "{deleteTargetQuestion.text}"
            </div>
          )}
          <p className="text-xs text-gray-500 mt-2.5">
            This change will sync automatically to attached Job Descriptions. Running interviews remain unaffected.
          </p>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
          <Button
            onClick={() => setDeleteTargetQuestion(null)}
            disabled={deletingQuestion}
            variant="outlined"
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600,
              color: '#475569',
              borderColor: '#cbd5e1',
              px: 2.5,
              '&:hover': { borderColor: '#94a3b8', backgroundColor: '#f8fafc' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            disabled={deletingQuestion}
            variant="contained"
            color="error"
            startIcon={deletingQuestion ? <CircularProgress size={16} color="inherit" /> : <Trash2 className="w-4 h-4" />}
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 700,
              px: 3,
              backgroundColor: '#ef4444',
              '&:hover': { backgroundColor: '#dc2626' },
            }}
          >
            {deletingQuestion ? 'Removing...' : 'Remove Question'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
